// Page Admin Audit Logs — historique des modifications (admin) — Option 1
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import DiagnosticsPanel from "@/components/admin/DiagnosticsPanel";
import { API_BASE } from "@/lib/apiBase";

interface AuditLog {
  id: number;
  table_name: string;
  record_id: number;
  action: "create" | "update" | "delete";
  user_id: number | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
}

export default function AuditLogsPage() {
  const t = useT();
  const [tableFilter, setTableFilter] = useState("");
  const [recordIdFilter, setRecordIdFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"logs" | "diagnostics">("logs");

  // Construire la query string
  const params = new URLSearchParams();
  if (tableFilter) params.append("table", tableFilter);
  if (recordIdFilter) params.append("record_id", recordIdFilter);
  if (actionFilter) params.append("action", actionFilter);
  if (userIdFilter) params.append("user_id", userIdFilter);
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  params.append("limit", "200");

  const { data, isLoading, error } = useQuery({
    queryKey: ["audit-logs", tableFilter, recordIdFilter, actionFilter, userIdFilter, dateFrom, dateTo],
    queryFn: async (): Promise<AuditLog[]> => {
      const r = await fetch(
        `${API_BASE}/v1/admin/audit-logs?${params.toString()}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`API ${r.status}`);
      return r.json();
    },
  });

  const reset = () => {
    setTableFilter("");
    setRecordIdFilter("");
    setActionFilter("");
    setUserIdFilter("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("audit.title")}</h1>
        <p className="text-muted-foreground">
          {t("audit.subtitle")}
        </p>
      </div>

      {/* Onglets : Journal d'audit | Santé des services */}
      <div className="flex gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setActiveTab("logs")}
          data-testid="audit-tab-logs"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === "logs"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("audit.tab_logs")}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("diagnostics")}
          data-testid="audit-tab-diagnostics"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === "diagnostics"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("audit.tab_diagnostics")}
        </button>
      </div>

      {activeTab === "diagnostics" ? (
        <DiagnosticsPanel />
      ) : (
        <>

      {/* Filtres */}
      <Card>
        <CardHeader>
          <CardTitle>{t("common.filter")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <div>
              <label className="text-xs">{t("audit.filter.table")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
              >
                <option value="">{t("audit.filter.all_actions")}</option>
                <option value="interventions">{t("audit.table.interventions")}</option>
                <option value="engins">{t("audit.table.engins")}</option>
                <option value="type_engins">{t("audit.table.type_engins")}</option>
                <option value="articles">{t("audit.table.articles")}</option>
                <option value="demandes_achat">{t("audit.table.demandes_achat")}</option>
                <option value="users">{t("audit.table.users")}</option>
              </select>
            </div>
            <div>
              <label className="text-xs">{t("audit.filter.record_id")}</label>
              <input
                type="number"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={recordIdFilter}
                onChange={(e) => setRecordIdFilter(e.target.value)}
                placeholder="ex: 5"
              />
            </div>
            <div>
              <label className="text-xs">{t("audit.filter.action")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              >
                <option value="">{t("audit.filter.all_actions")}</option>
                <option value="create">create</option>
                <option value="update">update</option>
                <option value="delete">delete</option>
              </select>
            </div>
            <div>
              <label className="text-xs">{t("audit.filter.user_id")}</label>
              <input
                type="number"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
                placeholder="ex: 1"
              />
            </div>
            <div>
              <label className="text-xs">{t("audit.filter.date_from")}</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs">{t("audit.filter.date_to")}</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex items-end col-span-full">
              <Button variant="outline" onClick={reset}>
                {t("maintenance.filter.reset")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {data ? t("audit.entries_count").replace("{count}", String(data.length)) : t("audit.loading")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              {t("audit.loading")}
            </div>
          ) : error ? (
            <div className="py-8 text-red-600">{(error as Error).message}</div>
          ) : !data || data.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {t("audit.empty")}
            </div>
          ) : (
            <div className="space-y-1">
              {data.map((log) => (
                <div
                  key={log.id}
                  className="rounded-md border border-border p-3 hover:bg-muted/30"
                >
                  <div
                    className="flex cursor-pointer items-center justify-between"
                    onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                  >
                    <div className="flex items-center gap-3 text-sm">
                      <span
                        className={
                          log.action === "create"
                            ? "rounded bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300"
                            : log.action === "update"
                              ? "rounded bg-blue-500/15 border border-blue-500/40 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300"
                              : "rounded bg-destructive/15 border border-destructive/40 px-2 py-0.5 text-xs text-destructive dark:text-red-300"
                        }
                      >
                        {log.action}
                      </span>
                      <span className="font-mono font-semibold">
                        {log.table_name}#{log.record_id}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        user={log.user_id ?? "?"}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.created_at).toLocaleString("fr-FR")}
                    </span>
                  </div>
                  {expanded === log.id && (
                    <div className="mt-2 space-y-2 text-xs">
                      {log.old_values && (
                        <div>
                          <div className="font-semibold text-muted-foreground">{t("audit.expand.old")}</div>
                          {/* bg-red-50 / bg-green-50 = blanc cassé, illisible en dark mode.
                              Utilise des couleurs sémantiques (bg-destructive/10 = léger tint
                              rouge adaptatif) + text-foreground explicite. */}
                          <pre className="overflow-x-auto rounded bg-destructive/10 p-2 text-[10px] text-foreground border-l-2 border-destructive/60">
                            {JSON.stringify(log.old_values, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log.new_values && (
                        <div>
                          <div className="font-semibold text-muted-foreground">{t("audit.expand.new")}</div>
                          <pre className="overflow-x-auto rounded bg-emerald-500/10 p-2 text-[10px] text-foreground border-l-2 border-emerald-500/60 dark:bg-emerald-500/15">
                            {JSON.stringify(log.new_values, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
}
